<script lang="ts">
  import { onMount } from 'svelte';
  import { Hospital, Farmacia, Pacientes, Ingresos, type Medicamento } from '../lib/api';

  let pendientes = $state<any[]>([]);
  let error = $state<string | null>(null);
  let info = $state<string | null>(null);
  let admPor = $state('');
  let motivoOm = $state('');
  let meds = $state<Medicamento[]>([]);
  let medsCargados = $state(false);
  let nombresPaciente = $state<Record<string, string>>({});
  let loading = $state(false);

  const nombreMed = (p: any) => p?.medicamentoNombre || meds.find(m => m.id === p?.medicamentoId)?.nombre || 'Medicamento';
  const nombrePac = (id?: string) => nombresPaciente[id ?? ''] ?? 'Paciente';

  function fmtFecha(iso?: string) {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString('es-GT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  }

  async function cargar() {
    loading = true;
    error = null;
    try {
      const data = await Hospital.kardexPendientes();
      const todos = Array.isArray(data) ? data : [];
      let filtrarPorIngreso = false;
      let activos = new Set<string>();
      try {
        const ingresos = await Ingresos.activos();
        activos = new Set(
          (Array.isArray(ingresos) ? ingresos : [])
            .map(i => i.pacienteId)
            .filter((id): id is string => !!id),
        );
        filtrarPorIngreso = true;
      } catch {
        // Si camas/ingresos falla, no ocultamos el kárdex completo.
      }
      pendientes = filtrarPorIngreso
        ? todos.filter(p => p?.pacienteId && activos.has(p.pacienteId))
        : todos;
      if (!medsCargados) {
        meds = await Farmacia.meds().catch(() => []);
        medsCargados = true;
      }
      const ids = [...new Set(pendientes.map(p => p.pacienteId as string | undefined).filter(Boolean) as string[])];
      const nombres: Record<string, string> = {};
      await Promise.all(ids.map(async id => {
        try {
          const p = await Pacientes.uno(id);
          nombres[id] = [p.nombres, p.apellidos].filter(Boolean).join(' ').trim() || (p.cui ? `CUI ${p.cui}` : 'Paciente');
        } catch { nombres[id] = 'Paciente'; }
      }));
      nombresPaciente = nombres;
    } catch (e: any) { error = e?.message ?? String(e); }
    finally { loading = false; }
  }
  onMount(() => { void cargar(); });
  async function administrar(p: any) {
    if (!admPor.trim()) { error = 'Indica quién administra (enfermera / código).'; return; }
    error = null;
    try {
      await Hospital.administrar(p.id, admPor.trim());
      if (p.medicamentoId && p.pacienteId) {
        const cant = Number(String(p.dosis ?? '1').replace(/[^\d.]/g, '')) || 1;
        await Farmacia.dispensar({
          medicamentoId: p.medicamentoId,
          pacienteId: p.pacienteId,
          cantidad: cant,
          dispensadoPor: admPor.trim(),
        }).catch(() => {});
      }
      await cargar();
    } catch (e: any) { error = e?.message; }
  }
  async function omitir(id: string) {
    let motivo = motivoOm.trim();
    if (!motivo) {
      const escrito = typeof window !== 'undefined'
        ? window.prompt('Motivo de omisión (obligatorio):', '')
        : null;
      if (!escrito?.trim()) {
        error = 'Para omitir hay que indicar el motivo (arriba o en el cuadro).';
        return;
      }
      motivo = escrito.trim();
      motivoOm = motivo;
    }
    error = null;
    info = null;
    try {
      await Hospital.omitir(id, motivo);
      motivoOm = '';
      info = 'Dosis omitida.';
      await cargar();
    } catch (e: any) { error = e?.message ?? String(e); }
  }
</script>

<section>
  <header class="view-header">
    <div>
      <h2>Kárdex de enfermería</h2>
      <p class="muted">Solo pacientes con ingreso activo. Al administrar se descuenta stock (FEFO). Para omitir, escriba el motivo.</p>
    </div>
    <button onclick={cargar} disabled={loading}>{loading ? '…' : 'Recargar'}</button>
  </header>
  {#if error}<p class="err">⚠ {error}</p>{/if}
  {#if info}<p class="ok">{info}</p>{/if}

  <div class="panel">
    <div class="row">
      <label class="field"><span>Enfermera firma</span><input bind:value={admPor} placeholder="Nombre / código"/></label>
      <label class="field grow"><span>Motivo omisión</span><input bind:value={motivoOm} placeholder="Obligatorio para Omitir · ej. Paciente NPO"/></label>
    </div>
  </div>

  <div class="panel">
    <h3>Pendientes de administración</h3>
    <table>
      <thead><tr><th>Paciente</th><th>Medicamento</th><th>Dosis</th><th>Vía</th><th>Programada</th><th>Estado</th><th></th></tr></thead>
      <tbody>
        {#each pendientes as p, i (p.id ?? i)}
          <tr>
            <td>{nombrePac(p.pacienteId)}</td>
            <td>{nombreMed(p)}</td>
            <td>{p.dosis ?? '—'}</td>
            <td>{p.via ?? '—'}</td>
            <td>{fmtFecha(p.horaProgramada)}</td>
            <td>{p.estado}</td>
            <td>
              <button class="ok" onclick={() => administrar(p)}>Administrar</button>
              <button class="warn" onclick={() => omitir(p.id)}>Omitir</button>
            </td>
          </tr>
        {:else}<tr><td colspan="7" class="empty">Sin pendientes de pacientes con ingreso activo.</td></tr>{/each}
      </tbody>
    </table>
  </div>
</section>

<style>
  .view-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; flex-wrap: wrap; margin-bottom: 1rem; }
  .view-header h2 { margin: 0; font-size: 1.4rem; color: #111827; }
  .muted { margin: 0.2rem 0 0; color: #6b7280; font-size: 0.85rem; }
  button { padding: .5rem .9rem; border: 0; background: linear-gradient(135deg, #15803d, #16a34a); color: #fff; border-radius: 8px; cursor: pointer; font: inherit; font-weight: 600; box-shadow: 0 4px 14px rgba(22,163,74,0.3); transition: transform 0.15s; }
  button:hover { transform: translateY(-1px); }
  .panel { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 1.25rem; box-shadow: 0 1px 3px rgb(0 0 0 / 5%); margin: 0 0 1rem; }
  .panel h3 { margin: 0 0 .5rem; font-size: 1rem; color: #166534; }
  .row { display: flex; gap: .75rem; flex-wrap: wrap; align-items: flex-end; }
  .field { display: flex; flex-direction: column; gap: .3rem; }
  .field.grow { flex: 1; min-width: 14rem; }
  .field span { font-size: .72rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: .03em; }
  .row input { padding: .5rem .65rem; border: 1px solid #e5e7eb; border-radius: 8px; font: inherit; background: #fff; color: #111827; }
  .row input:focus { outline: none; border-color: #16a34a; box-shadow: 0 0 0 3px rgba(22,163,74,0.12); }
  table { width: 100%; border-collapse: collapse; }
  th { text-align: left; font-size: .7rem; text-transform: uppercase; letter-spacing: .03em; color: #6b7280; padding: .55rem .8rem; background: #f0fdf4; }
  td { padding: .5rem .8rem; border-top: 1px solid #f3f4f6; text-align: left; font-size: .9rem; color: #111827; }
  .err { color: #dc2626; font-weight: 600; }
  .ok { color: #166534; font-weight: 600; }
  .empty { color: #9ca3af; text-align: center; font-style: italic; }
  td button { padding: .3rem .6rem; border-radius: 6px; border: 0; color: #fff; cursor: pointer; margin-right: .3rem; font-size: .85rem; box-shadow: none; font-weight: 600; }
  td button.ok { background: #16a34a; color: #fff; }
  .warn { background: #d97706; }
  td button:hover { transform: none; }
</style>
